import { useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  Loader2,
  Plus,
  Trash2,
  GraduationCap,
  ShoppingBag,
  FileText,
  RefreshCw,
  Bot,
  Truck,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import type { WizardState } from './useWizardState';

interface StepProps {
  state: WizardState;
  updateState: (updates: Partial<WizardState>) => void;
}

interface CourseCard {
  name: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  price: number;
  description: string;
  moduleCount: number;
}

const COURSE_REGEX = /course|lesson|training|education|learning/i;

function isCoursSite(description: string): boolean {
  return COURSE_REGEX.test(description);
}

export default function WizardStep5Products({ state, updateState }: StepProps) {
  const isSaas = state.siteType === 'saas';
  const isCourse = !isSaas && isCoursSite(state.businessDescription);
  const isDropship = !isSaas && !!state.features?.dropshipping;
  const [courseLoading, setCourseLoading] = useState(false);
  const courseFetched = useRef(false);

  // Parse courses from state
  const courses: CourseCard[] = (() => {
    if (!state.courseDescriptions) return [];
    try {
      return JSON.parse(state.courseDescriptions);
    } catch {
      return [];
    }
  })();

  function setCourses(updated: CourseCard[]) {
    updateState({ courseDescriptions: JSON.stringify(updated) });
  }

  // Fetch AI course suggestions on mount for course sites
  useEffect(() => {
    if (!isCourse) return;
    if (courseFetched.current) return;
    courseFetched.current = true;
    if (courses.length === 0) {
      fetchCourseSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchCourseSuggestions() {
    setCourseLoading(true);
    try {
      const res = await api.post<any>('/admin/ai/generate/wizard-assist', {
        action: 'suggest-course-outline',
        context: {
          storeName: state.storeName,
          businessDescription: state.businessDescription,
          tagline: state.tagline,
        },
      });
      const aiCourses = res.courses || res.result?.courses || [];
      if (aiCourses.length) {
        setCourses(aiCourses.map((c: any, i: number) => ({
          name: c.name || '',
          level: c.level || (['beginner', 'intermediate', 'advanced'][i] || 'beginner'),
          price: c.price || [49, 99, 199][i] || 99,
          description: c.description || '',
          moduleCount: c.moduleCount || c.module_count || 5,
        })));
      }
    } catch {
      setCourses([
        {
          name: '',
          level: 'beginner',
          price: 49,
          description: '',
          moduleCount: 5,
        },
      ]);
    } finally {
      setCourseLoading(false);
    }
  }

  function updateCourse(index: number, field: keyof CourseCard, value: any) {
    const updated = [...courses];
    updated[index] = { ...updated[index], [field]: value };
    setCourses(updated);
  }

  function addCourse() {
    setCourses([
      ...courses,
      { name: '', level: 'beginner', price: 49, description: '', moduleCount: 5 },
    ]);
  }

  function removeCourse(index: number) {
    const updated = [...courses];
    updated.splice(index, 1);
    setCourses(updated);
  }

  // SaaS marketing sites skip the products/courses step entirely.
  // The wizard generates marketing pages (hero, features, pricing, customers,
  // about) via the saas-content-generator instead of seeding a product catalog.
  if (isSaas) {
    return (
      <div className="space-y-6">
        <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
          <div className="px-5 py-4 bg-stone-50 border-b border-stone-200">
            <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Content generation
            </h2>
            <p className="text-xs text-text-muted mt-1">
              No products or courses to configure for a SaaS marketing site.
            </p>
          </div>
          <div className="p-6 space-y-4 text-sm text-text-secondary leading-relaxed">
            <p>
              The wizard will generate the following for{' '}
              <strong className="text-text-primary">{state.storeName || 'your site'}</strong> on the next step:
            </p>
            <ul className="list-disc list-inside space-y-1.5 pl-2">
              <li>Hero headline and CTA tailored to your tagline</li>
              <li>Value propositions (from Step 4)</li>
              <li>Features, Customers, and About pages as HTML</li>
              <li>3 AI-generated blog posts</li>
              <li>Logo, OG image, and homepage hero image</li>
              <li>Privacy policy, terms of service, and refund pages</li>
              <li>Seeded pricing tiers, testimonials, stats, and FAQ sections</li>
            </ul>
            <p className="text-xs text-text-muted pt-2">
              You can edit everything via the admin panel after the site is deployed.
              Click Continue to review and launch.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Products / Courses Card ── */}
      <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
        <div className="px-5 py-4 bg-stone-50 border-b border-stone-200">
          <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
            {isCourse ? (
              <><GraduationCap className="w-5 h-5 text-primary" /> Courses</>
            ) : (
              <><ShoppingBag className="w-5 h-5 text-primary" /> Products</>
            )}
          </h2>
        </div>
        <div className="p-5">
          {isCourse ? (
            /* ── Course Planning ── */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-text-muted">Plan the courses your site will offer.</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchCourseSuggestions}
                  disabled={courseLoading}
                >
                  {courseLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Regenerate
                </Button>
              </div>

              {courseLoading && courses.length === 0 ? (
                <div className="space-y-4 animate-pulse">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-40 bg-stone-100 rounded-lg" />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {courses.map((course, i) => (
                    <div
                      key={i}
                      className="border border-stone-200 rounded-lg p-4 bg-white space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
                          Course {i + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeCourse(i)}
                          className="p-1 text-red-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            Course Name
                          </label>
                          <input
                            type="text"
                            value={course.name}
                            onChange={(e) => updateCourse(i, 'name', e.target.value)}
                            placeholder="e.g. Introduction to Photography"
                            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            Level
                          </label>
                          <select
                            value={course.level}
                            onChange={(e) => updateCourse(i, 'level', e.target.value)}
                            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            Suggested Price ($)
                          </label>
                          <input
                            type="number"
                            value={course.price}
                            onChange={(e) =>
                              updateCourse(i, 'price', parseFloat(e.target.value) || 0)
                            }
                            min={0}
                            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            Modules
                          </label>
                          <input
                            type="number"
                            value={course.moduleCount}
                            onChange={(e) =>
                              updateCourse(i, 'moduleCount', parseInt(e.target.value) || 1)
                            }
                            min={1}
                            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-text-secondary mb-1">
                            Description
                          </label>
                          <textarea
                            rows={2}
                            value={course.description}
                            onChange={(e) => updateCourse(i, 'description', e.target.value)}
                            placeholder="What will students learn?"
                            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button variant="ghost" onClick={addCourse}>
                    <Plus className="w-4 h-4" />
                    Add Course
                  </Button>
                </div>
              )}
            </div>
          ) : isDropship ? (
            /* ── Dropshipping ── */
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Dippy Dropship Agent</p>
                <p className="text-sm text-text-muted mt-0.5">
                  The Dippy dropship agent will source real products from suppliers for your store.
                  No sample products needed — Dippy handles product discovery, pricing, and catalog management.
                </p>
              </div>
            </div>
          ) : (
            /* ── Sample Products ── */
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-sm text-text-muted mb-3">
                  AI will generate sample products to populate your store on launch day.
                </p>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-text-secondary">Sample products:</label>
                  <input
                    type="number"
                    value={state.productCount}
                    onChange={(e) => {
                      const val = Math.min(20, Math.max(1, parseInt(e.target.value) || 1));
                      updateState({ productCount: val });
                    }}
                    min={1}
                    max={20}
                    className="w-20 rounded-lg border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <span className="text-xs text-text-muted">1–20</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Blog Agent Card ── */}
      <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
        <div className="px-5 py-4 bg-stone-50 border-b border-stone-200">
          <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Blog
          </h2>
        </div>
        <div className="p-5 space-y-4">
          {/* Blog Agent info */}
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <Bot className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                Blog Agent — publishes 2x per week
              </p>
              <p className="text-sm text-text-muted mt-0.5">
                The AI blog agent will autonomously pick topics and publish new blog posts on
                Tuesdays and Fridays. You can adjust the schedule, review posts, or add your own
                topics anytime at <span className="font-medium text-text-secondary">/admin/blog/agent</span>.
              </p>
            </div>
          </div>

          {/* Seed post count */}
          <div className="flex items-center gap-3 pl-12">
            <label className="text-sm font-medium text-text-secondary">Seed posts for launch:</label>
            <input
              type="number"
              value={state.blogPostCount}
              onChange={(e) => {
                const val = Math.min(10, Math.max(0, parseInt(e.target.value) || 0));
                updateState({ blogPostCount: val });
              }}
              min={0}
              max={10}
              className="w-20 rounded-lg border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <span className="text-xs text-text-muted">0–10 initial posts</span>
          </div>
        </div>
      </div>

      {/* ── Generate AI Content Card ── */}
      <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
        <div className="px-5 py-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={state.generateContent}
              onChange={(e) => updateState({ generateContent: e.target.checked })}
              className="mt-1 h-4 w-4 rounded border-stone-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-text-primary flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Generate AI content
              </span>
              <p className="text-xs text-text-muted mt-0.5">
                {isCourse
                  ? 'Generate course content, blog posts, and images with AI. Takes 1–2 minutes.'
                  : isDropship
                    ? 'Generate blog posts, pages, and images with AI. Takes 1–2 minutes.'
                    : 'Generate products, blog posts, and images with AI. Takes 1–2 minutes.'}
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
}
